/**
 * Page « Mes demandes » du dashboard client (/dashboard/requests).
 *
 * Liste tabulaire des dossiers avec référence, type, statut, adresse, date,
 * progression — chaque ligne ouvre le détail dossier.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { getStatusColor } from '@/features/dashboard/status-color';
import {
  requestTypeLabels,
  statusLabels,
} from '@/features/projects/labels';
import { computePayloadProgressPercent } from '@/features/projects/booking-progress';
import { listProjects } from '@/features/projects/projects-api';
import { listMyAppointments } from '@/features/appointments/appointments-api';

export function DashboardRequestsPage() {
  const q = useQuery({
    queryKey: ['projects', 'mine'],
    queryFn: listProjects,
  });
  const appointments = useQuery({
    queryKey: ['appointments', 'mine'],
    queryFn: listMyAppointments,
  });
  // Dossiers où le client doit choisir un créneau (action requise).
  const slotActionNeeded = new Set(
    (appointments.data ?? [])
      .filter((a) => a.status === 'creneaux_proposes')
      .map((a) => a.projectId),
  );

  const list = q.data ?? [];

  return (
    <DashboardLayout title="Mes demandes">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle>Suivi de vos demandes</CardTitle>
            <CardDescription>
              {list.length} dossier{list.length > 1 ? 's' : ''} sur votre
              espace.
            </CardDescription>
          </div>
          <Button asChild>
            <Link to="/projects/new">
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle demande
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : list.length === 0 ? (
            <div className="rounded-md border bg-white p-6 text-center text-sm text-gray-500">
              Aucune demande pour le moment.{' '}
              <Link to="/projects/new" className="text-bcl-blue underline">
                Démarrer un projet
              </Link>
              .
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Adresse</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead>Progression</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => {
                  const address = p.info
                    ? `${p.info.address.line1}, ${p.info.address.postalCode} ${p.info.address.city}`
                    : 'Adresse à compléter';
                  const progress = computePayloadProgressPercent(p);
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.reference}</TableCell>
                      <TableCell>{requestTypeLabels[p.requestType]}</TableCell>
                      <TableCell>
                        {slotActionNeeded.has(p.id) ? (
                          <Badge className="animate-pulse border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100">
                            ⚠ Choisissez votre date
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className={getStatusColor(statusLabels[p.status])}
                          >
                            {statusLabels[p.status]}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{address}</TableCell>
                      <TableCell>
                        {new Date(p.createdAt).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={progress} className="h-2 w-32" />
                          <span className="text-xs text-gray-500">
                            {progress}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/projects/${p.id}`}>
                            Détails
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
